import { ReactNode, useCallback, useMemo, useState } from 'react';

import { faPaperPlane, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled, { css } from 'styled-components';

import AuthorizedButton from 'components/inputs/AuthorizedButton';
import { generatePath, useNavigate } from 'react-router-dom';
import CreateCaseModal from 'components/CreateCaseModal';
import { useCurrentRouteCaseRoute } from 'hooks/useCurrentRouteCaseType';
import { CaseTypeEnum } from 'api/model';
import FilterPicker from 'components/filterBar/FilterPicker';
import FilterSearchInput from 'components/filterBar/FilterSearchInput';
import CompanyPicker from 'components/CompanyPicker';
import { fakeSelectClassName } from 'components/inputs/SearchableSelect';
import MultiSelect, { Option } from 'components/inputs/MultiSelect';
import useToFromAddressAssignmentFilter from './useToFromAddresAssignmentFilter';
import {
  formatHubCount,
  HubAssignmentCount,
  HubFilterValue,
  OTHER_HUBS_FILTER_VALUE,
} from './utils';
import Roles from 'constants/Roles';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  margin: 10px 10px;
`;

const MyAuthorizedButton = styled(AuthorizedButton)`
  height: 30px;
  padding: 0 10px;
  margin-right: 10px;
  font: inherit;
  white-space: nowrap;

  & > *:first-child {
    margin-right: 6px;
    font-size: 12px;
  }
`;

const SearchInputs = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  margin-left: auto;
`;

const MyFilterSearchInput = styled(FilterSearchInput)`
  margin-left: 20px;
`;

// Från-/Till-väljarnas inputfält får samma mått som hubbfältet: 34 px höga, samma innerpadding
// (bredden 180 px kommer redan från inputStyle). Listorna som fälls ut rörs inte. Stilen måste
// sättas via en wrapper — CompanyPicker tar inte emot className, så styled(CompanyPicker) var
// verkningslöst. $empty: "Alla" är ett riktigt alternativ i CompanyPicker men ska se ut som
// hubbfältets placeholder (grå) när inget företag är valt.
const CompanyFilter = styled.span<{ $empty: boolean }>`
  .${fakeSelectClassName} {
    height: 34px;
    align-items: center;
    padding: 5px 10px;
    padding-right: 5px;

    /* Långa företagsnamn ska inte radbrytas och spränga den fasta höjden. */
    & > div {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      ${({ $empty, theme }) =>
        $empty &&
        css`
          color: ${theme.colors.foreground.tertiary};
        `}
    }
  }
`;

// Samma höjd som företagsväljarna bredvid. Markeras när något är ibockat, som appens övriga
// MultiSelect-filter.
const HubMultiSelect = styled(MultiSelect)`
  height: 34px;
  min-width: 180px;
  align-items: center;
  ${({ value, theme }) =>
    value.size > 0 &&
    css`
      background: ${theme.colors.background.highlight};
    `}
` as typeof MultiSelect;

// Räknarna visas bara i den utfällda listan (labelRender), inte i det ibockade fältet, som är
// för smalt för dem. Gråtonade så att de skiljer sig från hubbnamnet.
const HubCount = styled.span`
  margin-left: 6px;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
`;

const renderHubOptionLabel = (
  option: Option<HubFilterValue>,
  count: HubAssignmentCount | undefined
): ReactNode => (
  <>
    {option.label}
    <HubCount>{formatHubCount(count)}</HubCount>
  </>
);

interface Props {
  onSendToFieldTestersClick(): void;
  fromToFilter: ReturnType<typeof useToFromAddressAssignmentFilter>;
  /** Antal ben per hubbalternativ (totalt / planerade vidaretransporter), se countAssignmentsByHub. */
  hubCounts: Map<HubFilterValue, HubAssignmentCount>;
}

const PendingAssignmentsFilterBar: React.FC<Props> = ({
  onSendToFieldTestersClick,
  fromToFilter,
  hubCounts,
}) => {
  const currentRouteCaseRoute = useCurrentRouteCaseRoute();
  const navigate = useNavigate();
  const [showAdd, setShowAdd] = useState<boolean>(false);

  // "Övriga" överst, sedan hubbarna i namnordning (sorterade i hooken).
  const hubOptions = useMemo(
    (): Option<HubFilterValue>[] => [
      { label: 'Övriga (ej hubb)', value: OTHER_HUBS_FILTER_VALUE },
      ...fromToFilter.hubs.map((hub) => ({
        label: hub.name,
        value: hub.companyID,
      })),
    ],
    [fromToFilter.hubs]
  );

  const renderHubOption = useCallback(
    (option: Option<HubFilterValue>) =>
      renderHubOptionLabel(option, hubCounts.get(option.value)),
    [hubCounts]
  );

  const handleCaseCreated = (caseId: number) => {
    setShowAdd(false);
    navigate(generatePath(currentRouteCaseRoute, { id: String(caseId) }));
  };

  return (
    <Wrapper>
      <FilterPicker />

      <MyAuthorizedButton
        icon={<FontAwesomeIcon icon={faPaperPlane} />}
        onClick={onSendToFieldTestersClick}
        roles={[Roles.Admin]}
      >
        Skicka uppdrag till fälttestare
      </MyAuthorizedButton>

      <MyAuthorizedButton
        icon={<FontAwesomeIcon icon={faPlus} />}
        onClick={() => {
          setShowAdd(true);
        }}
        roles={[Roles.Admin]}
      >
        Lägg till
      </MyAuthorizedButton>

      <SearchInputs>
        <span>
          Hubb:{' '}
          <HubMultiSelect
            labelRender={renderHubOption}
            onChange={fromToFilter.setSelectedHubs}
            options={hubOptions}
            placeholder="Alla"
            value={fromToFilter.selectedHubs}
          />
        </span>
        <CompanyFilter $empty={!fromToFilter.selectedFromCompany}>
          Från:{' '}
          <CompanyPicker
            nullValueLabel="Alla"
            onCompanySelected={fromToFilter.setSelectedFromCompany}
            selectedCompanyId={
              fromToFilter.selectedFromCompany?.companyID ?? null
            }
          />
        </CompanyFilter>
        <CompanyFilter $empty={!fromToFilter.selectedToCompany}>
          Till:{' '}
          <CompanyPicker
            nullValueLabel="Alla"
            onCompanySelected={fromToFilter.setSelectedToCompany}
            selectedCompanyId={
              fromToFilter.selectedToCompany?.companyID ?? null
            }
          />
        </CompanyFilter>
        <MyFilterSearchInput />
      </SearchInputs>

      {showAdd && (
        <CreateCaseModal
          caseType={CaseTypeEnum.InternalDelivery}
          onCaseCreated={handleCaseCreated}
          onClose={() => {
            setShowAdd(false);
          }}
        />
      )}
    </Wrapper>
  );
};

export default PendingAssignmentsFilterBar;
